import { Dictionary } from '@reduxjs/toolkit';
import BigNumber from 'bignumber.js';

import SETTINGS from '#/config/global-settings';

import * as MarketFn from '#/features/market/functions';

import { Fill, FillSide } from '#/api/fills';
import { Market } from '#/api/markets';
import { Transaction } from '#/api/transactions';
import { TradeBust } from '#/api/trade-busts';

import { Maybe, Nullable } from '#/utils/types';
import { shortDateFormat, timeFormat } from '#/utils/date';
import capitalizeText from '#/utils/capitalizeText';

export type FillStatus = 'PENDING' | 'COMPLETE' | 'BUST' | undefined;

export interface FillView {
  readonly id: Fill['id'];
  readonly side: FillSide;
  readonly createdTime: string;
  readonly createdDate: string;
  readonly liquidity: string;
  readonly market: string;
  readonly price: string;
  readonly size: string;
  readonly fee: string;

  // Derived:
  readonly priceTotal: string;
  readonly status: {
    readonly value: FillStatus;
    readonly label: string;
  };
  readonly txHash: Nullable<string>;
}

export function fillView(
  fill: Fill,
  markets: Dictionary<Market>,
  transaction: Transaction | undefined,
  tradeBust: TradeBust | undefined,
): FillView {
  const market = markets[fill.market];

  if (market == null) return emptyFillView(fill);

  const baseCurrencyDecimalPlaces = MarketFn.baseCurrencyDecimalPlaces(market);
  const quoteCurrencyDecimalPlaces =
    MarketFn.quoteCurrencyDecimalPlaces(market);

  const createdTime = timeFormat(fill.created_at);

  const createdDate = shortDateFormat(fill.created_at);

  const liquidity = capitalizeText(fill.liquidity);

  const price = fill.price.toFormat(quoteCurrencyDecimalPlaces);

  const size = `${fill.size.toFormat(baseCurrencyDecimalPlaces)} ${
    market.base_currency
  }`;

  const fee = feeView(fill);

  const priceTotal = `$${fill.size
    .times(fill.price)
    .toFormat(SETTINGS.fiat.USD.scale)}`;

  const status = statusView(transaction, tradeBust);

  const txHash = txHashView(transaction);

  return {
    id: fill.id,
    side: fill.side,
    createdTime,
    createdDate,
    liquidity,
    market: fill.market,
    price,
    size,
    fee,
    priceTotal,
    status,
    txHash,
  };
}

const emptyFillView = (fill: Fill): FillView => ({
  id: fill.id,
  side: fill.side,
  createdTime: timeFormat(fill.created_at),
  createdDate: shortDateFormat(fill.created_at),
  liquidity: capitalizeText(fill.liquidity),
  market: fill.market,
  price: '-',
  size: '-',
  fee: '-',
  priceTotal: '-',
  status: {
    value: undefined,
    label: '-',
  },
  txHash: '',
});

function txHashView(transaction: Maybe<Transaction>) {
  if (transaction == null) return null;
  if (transaction.hash === '') return null;
  return transaction.hash;
}

export function statusView(
  transaction: Transaction | undefined,
  tradeBust: TradeBust | undefined,
): FillView['status'] {
  if (tradeBust != null) {
    return { value: 'BUST', label: 'Bust' };
  }
  if (transaction == null) {
    return { value: undefined, label: '-' };
  }
  return TX_STATE_TO_FILL_STATUS_MAP[transaction.state];
}

export function feeView(fill: Fill): FillView['fee'] {
  const rounded = fill.fee.decimalPlaces(4, BigNumber.ROUND_UP);
  return `$${rounded.toFormat()}`;
}

const TX_STATE_TO_FILL_STATUS_MAP: Record<
  Transaction['state'],
  FillView['status']
> = {
  NOT_RECEIVED: { value: 'PENDING', label: 'Pending' },
  RECEIVED: { value: 'PENDING', label: 'Pending' },
  PENDING: { value: 'COMPLETE', label: 'Complete' },
  ACCEPTED_ON_L2: { value: 'COMPLETE', label: 'Complete' },
  ACCEPTED_ON_L1: { value: 'COMPLETE', label: 'Complete' },
  REJECTED: { value: 'BUST', label: 'Bust' },
};
